import { useTranslate } from 'hooks/useTranslate';
import { ChangeEvent, DragEvent, FC, useRef, useState } from 'react';

import errorSwal from 'utils/errorSwal';
import asyncActionSwal from 'utils/asyncActionSwal';

import { isDocumentFileAllowed } from 'utils/isDocumentFileAllowed';

import './UploadDocuments.scss';

const ALLOWED_FILE_TYPES = 'application/pdf';

type UploadDocumentsProps = {
  idIndex?: number;
  label?: string;
  maxFiles?: number;
  uploadFiles: (files: File[]) => Promise<void> | void; // eslint-disable-line
};

const UploadDocuments: FC<UploadDocumentsProps> = ({
  idIndex = 0,
  label,
  maxFiles = 10,
  uploadFiles,
}) => {
  const { t } = useTranslate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);

  const resetInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasInvalidFiles = async (pdfFiles: File[]) => {
    const validationResults = await Promise.all(
      pdfFiles.map((file) => isDocumentFileAllowed(file))
    );
    return validationResults.some((isValid) => !isValid);
  };

  const handleUpload = async (files: File[]) => {
    const pdfFiles = files.filter((file) => file.type === 'application/pdf');

    if (pdfFiles.length > maxFiles) {
      errorSwal(t('TooManyFiles', { max: maxFiles }));
      resetInput();
      return;
    }

    const invalidFiles = await hasInvalidFiles(pdfFiles);
    if (invalidFiles) {
      errorSwal(t('InvalidFile'));
      resetInput();
      return;
    }

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelDocument'),
      swalElementName: t('OnlyPDFDocumentsWillBeUploaded'),
      swalInfoText: '',
      swalConfirmButtonText: t('Upload'),
      callback: async () => {
        try {
          setIsUploading(true);
          await uploadFiles(pdfFiles);
          setIsUploading(false);
          setUploadDone(true);
          setTimeout(() => setUploadDone(false), 2000);
        } catch (error) {
          errorSwal(t('ErrorUploadingDocuments'));
        }
      },
    });
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = [...e.dataTransfer.files];
    await handleUpload(droppedFiles);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleChooseDocument = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    setTimeout(() => handleUpload(selectedFiles), 100);
  };

  const dropZoneText = () => {
    if (isUploading) return t('Uploading');
    if (uploadDone) return t('UploadComplete');
    return t('DragDocumentsToUpload');
  };

  return (
    <div className="form-group">
      {label && (
        <label className="form-label" htmlFor={`file-input-styled-${idIndex}`}>
          {t(label)}
        </label>
      )}

      <div
        className={`upload-drop-zone ${
          isDragOver ? 'upload-drop-zone--active' : ''
        } ${uploadDone ? 'upload-drop-zone--done' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="upload-drop-zone__icon">
          {uploadDone && (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#10b981"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
          {isUploading && (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              className="upload-drop-zone__spinner"
            >
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          )}
          {!uploadDone && !isUploading && (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          )}
        </div>

        <span className="upload-drop-zone__text">{dropZoneText()}</span>

        <label
          htmlFor={`file-input-styled-${idIndex}`}
          className="upload-drop-zone__btn"
        >
          {t('Choose')}
        </label>

        <input
          id={`file-input-styled-${idIndex}`}
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleChooseDocument}
          accept={ALLOWED_FILE_TYPES}
          multiple
        />
      </div>
    </div>
  );
};

export default UploadDocuments;
